import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Phone, Star, MapPin } from "lucide-react";
import { scrollToId, PHONE, PHONE_TEL, SERVICE_AREAS, PROMO } from "@/lib/site";

const NAV = [
  { label: PROMO.name, id: "fall-special", testid: "nav-fall-special-link" },
  { label: "Services", id: "services", testid: "nav-services-link" },
  { label: "Schools", id: "school-spirit", testid: "nav-schools-link" },
  { label: "Why Us", id: "why-us", testid: "nav-why-us-link" },
  { label: "Coverage", id: "coverage", testid: "nav-coverage-link" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const goSection = (id) => {
    if (location.pathname !== "/") navigate("/", { state: { scrollTo: id } });
    else scrollToId(id);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <div
        data-testid="fall-special-banner"
        className="bg-gradient-to-r from-fall-orange via-fall-amber to-fall-orange px-4 py-2 text-center"
      >
        <button
          data-testid="fall-special-banner-claim"
          onClick={() => goSection("fall-special")}
          className="text-xs sm:text-sm font-bold font-display uppercase tracking-widest text-brand-navy hover:opacity-80 transition-opacity"
        >
          {PROMO.name} — Comforters $19.99 each when you clean 2+ · Ends {PROMO.endsLabel} →
        </button>
      </div>

      <div
        className={`border-b border-white/10 text-white transition-colors duration-300 ${
          scrolled ? "bg-brand-navy/90 backdrop-blur-xl shadow-lg shadow-brand-navy/30" : "bg-brand-navy/60 backdrop-blur-md"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <button
            data-testid="nav-brand-logo"
            onClick={() =>
              location.pathname !== "/"
                ? navigate("/")
                : window.__lenis?.scrollTo(0, { duration: 1.2 })
            }
            className="flex items-center gap-2.5"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-fall-amber shadow-[0_0_20px_rgba(245,158,11,0.45)]">
              <Star className="h-5 w-5 fill-brand-navy text-brand-navy" />
            </span>
            <span className="text-left leading-none">
              <span className="block font-display text-lg font-extrabold tracking-tight">Oxford Cleaners</span>
              <span className="block text-[10px] font-semibold uppercase tracking-[0.25em] text-amber-300">Chandler, AZ</span>
            </span>
          </button>

          <nav className="hidden items-center gap-7 lg:flex">
            {NAV.map((item) => (
              <button
                key={item.id}
                data-testid={item.testid}
                onClick={() => goSection(item.id)}
                className="text-sm font-semibold text-slate-200 transition-colors hover:text-fall-amber"
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <a
              data-testid="nav-phone-button"
              href={PHONE_TEL}
              className="hidden items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm font-semibold transition-colors hover:border-fall-amber hover:text-fall-amber sm:flex"
            >
              <Phone className="h-4 w-4" />
              {PHONE}
            </a>
            <button
              data-testid="nav-book-pickup-button"
              onClick={() => goSection("book")}
              className="rounded-full bg-fall-amber px-4 py-2 text-sm font-bold text-brand-navy shadow-[0_0_20px_rgba(245,158,11,0.4)] transition-transform hover:scale-105 sm:px-5"
            >
              Book Free Pickup
            </button>
          </div>
        </div>
      </div>

      <div data-testid="header-areas-strip" className="border-t border-white/5 bg-brand-navy/80 backdrop-blur-md">
        <p className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-1.5 gap-y-1 px-4 py-1.5 text-center text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-300">
          <MapPin className="h-3 w-3 shrink-0 text-fall-amber" />
          <span className="text-slate-400">Free pickup &amp; delivery:</span>
          <span>{SERVICE_AREAS.filter((c) => c !== "Maricopa").join(" · ")} ·</span>
          <span className="rounded-full bg-fall-amber px-2 py-0.5 font-extrabold normal-case tracking-normal text-brand-navy">
            Maricopa — Now Serving!
          </span>
        </p>
      </div>
    </header>
  );
};
